import { z } from "zod/v4";

/** A field the API may omit or send as null; the portal always reads it as `T | null`. */
export function absentable<T extends z.ZodTypeAny>(schema: T) {
	return schema.nullish().transform((value): z.output<T> | null => value ?? null);
}

export const currencySchema = z.literal("EUR");
export const moneySchema = z.object({
	amountMinor: z.number().int().nonnegative(),
	currency: currencySchema,
});
export type Money = z.infer<typeof moneySchema>;

/** 19 / 7 / 0 percent. */
export const vatRateSchema = z.enum(["standard", "reduced", "exempt"]);
export type VatRate = z.infer<typeof vatRateSchema>;

export const levelSchema = z.enum(["beginner", "intermediate", "advanced", "allLevels"]);
export type Level = z.infer<typeof levelSchema>;

const youthAudience = (minMaxAge: number) =>
	z.object({
		kind: z.literal("youth"),
		maxAge: z.number().int().min(minMaxAge).max(30),
		minAge: z.number().int().min(0).max(30),
	});

/** What the API sends: it allows a youth range of 0..30, so a range ending at 0 is valid. */
export const audienceSchema = z.discriminatedUnion("kind", [
	z.object({ kind: z.literal("allAges") }),
	z.object({ kind: z.literal("adults") }),
	youthAudience(0),
]);

/** What the form sends: a youth range needs a real upper age. */
export const audienceInputSchema = z.discriminatedUnion("kind", [
	z.object({ kind: z.literal("allAges") }),
	z.object({ kind: z.literal("adults") }),
	youthAudience(1),
]);
export type Audience = z.infer<typeof audienceSchema>;
export type AudienceKind = Audience["kind"];

export const countryCodeSchema = z.string().regex(/^[A-Z]{2}$/, "ISO 3166-1 alpha-2");

export const placeSchema = z.object({
	address: absentable(z.string()),
	city: z.string().min(1),
	country: countryCodeSchema,
	lat: absentable(z.number()),
	lng: absentable(z.number()),
	name: z.string().min(1),
});
export type Place = z.infer<typeof placeSchema>;

export const PLACE_NAME_MAX_LENGTH = 200;
export const PLACE_ADDRESS_MAX_LENGTH = 300;
export const PLACE_CITY_MAX_LENGTH = 120;

/**
 * What the portal sends for a place. The form has no coordinates; an edit hands back the saved
 * pair, because the update replaces the whole place and would otherwise clear it.
 */
export const placeInputSchema = z
	.object({
		address: z.string().trim().max(PLACE_ADDRESS_MAX_LENGTH).optional(),
		city: z.string().trim().min(1).max(PLACE_CITY_MAX_LENGTH),
		country: countryCodeSchema,
		lat: z.number().min(-90).max(90).optional(),
		lng: z.number().min(-180).max(180).optional(),
		name: z.string().trim().min(1).max(PLACE_NAME_MAX_LENGTH),
	})
	// The API refuses exactly one of the pair (`PlaceError::Coordinates`).
	.refine((place) => (place.lat === undefined) === (place.lng === undefined), {
		message: "lat and lng go together",
		path: ["lat"],
	});
export type PlaceInput = z.infer<typeof placeInputSchema>;

export const staffRoleSchema = z.enum(["owner", "manager", "staff"]);
export type StaffRole = z.infer<typeof staffRoleSchema>;

export const SLUG_MIN_LENGTH = 3;
export const CAMP_SLUG_MAX_LENGTH = 60;
export const ORGANIZER_SLUG_MAX_LENGTH = 40;
export const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/** Lowercase words joined by single hyphens, 3 to 60 characters: a camp's address. */
export const slugSchema = z
	.string()
	.min(SLUG_MIN_LENGTH)
	.max(CAMP_SLUG_MAX_LENGTH)
	.regex(SLUG_PATTERN, "Lowercase letters, digits and single hyphens");
